import { connect, FormikValues } from 'formik'
import { cloneDeep } from 'lodash'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { string } from 'yup'

import { Text } from '@/components/Form'
import Label from '@/components/Label'
import { FormLabel, Row } from '@/components/StyledComponents'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/AutoCompleteVariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'
import { FieldTypes, KnnSearchStepData, StepData, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

const StyledVariableInputContainer = styled(VariableInputContainer)`
  padding-top: 10px;
`

const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`

const StyledText = styled(Text)`
  font-size: 0.9rem;

  &.form__field {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  label {
    grid-column: span 2;
  }

  input {
    width: 5rem;
  }

  input + span {
    margin-left: 0.5rem;
  }
`

type KnnSearchProps = {
  readOnly?: boolean
}

const getValidationSchema = (intl: IntlShape) => {
  return {
    question: string().required(intl.formatMessage(messages.questionRequired)),
    documentChunks: string().required(intl.formatMessage(messages.documentChunksRequired))
  }
}

const getInitialValues = (stepData: KnnSearchStepData): StepData => {
  return {
    question: stepData.question ?? '',
    documentChunks: stepData.documentChunks ?? '',
    numberOfNeighboursToReturn: stepData.numberOfNeighboursToReturn
  }
}

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)

  if (!Number.isInteger(updatedValues.stepData.numberOfNeighboursToReturn)) {
    updatedValues.stepData.numberOfNeighboursToReturn = 3
  }

  const outputJson = {
    neighbours: ['']
  }

  updatedValues.output = convertJSONtoSchema(outputJson)
  return updatedValues
}

const KnnSearchOperation = ({ formik, readOnly = false }: KnnSearchProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  const onSelectVariable = (variable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, variable.name)
  }

  return variables?.length > 0 ? (
    <>
      <Label className="mt-4" required>
        {intl.formatMessage(messages.question)}
      </Label>
      <Label className="text-sm text-gray-600">{intl.formatMessage(messages.questionSubtitle)}</Label>
      <StyledVariableInputContainer>
        <VariableAutoComplete
          readOnly={readOnly}
          name={'stepData.question'}
          variables={variables}
          validVariableTypes={[FieldTypes.OBJECT, FieldTypes.ANY]}
          placeholder={intl.formatMessage(messages.enterQuestion)}
          // @ts-expect-error
          onSelectOption={onSelectVariable}
          showValidationMessage={true}
          required
        />
      </StyledVariableInputContainer>
      <Label className="mt-4" required>
        {intl.formatMessage(messages.documentChunks)}
      </Label>
      <Label className="text-sm text-gray-600">{intl.formatMessage(messages.documentChunksSubtitle)}</Label>
      <StyledVariableInputContainer>
        <VariableAutoComplete
          readOnly={readOnly}
          name={'stepData.documentChunks'}
          variables={variables}
          validVariableTypes={[FieldTypes.ARRAY, FieldTypes.ANY]}
          placeholder={intl.formatMessage(messages.enterDocumentChunks)}
          // @ts-expect-error
          onSelectOption={onSelectVariable}
          showValidationMessage={true}
          required
        />
      </StyledVariableInputContainer>
      <StyledText
        className="field__input"
        key="numberOfNeighboursToReturn"
        name="stepData.numberOfNeighboursToReturn"
        disabled={readOnly}
        required={false}
        label={intl.formatMessage(messages.numberOfNeighboursToReturn)}
        subtitle={intl.formatMessage(messages.numberOfNeighboursToReturnSubtitle)}
        isnumberinput="true"
        type="number"
        min={1}
        placeholder={3}
        value={formik.values.stepData.numberOfNeighboursToReturn}
        onChange={formik.setFieldValue}
        data-testid="neighbours-to-return"
      />
    </>
  ) : (
    <StyledRow>
      <FormLabel>{intl.formatMessage(messages.arrayVariableErrorMessage)}</FormLabel>
    </StyledRow>
  )
}

export default connect(KnnSearchOperation)

export { getInitialValues, getValidationSchema, getNetworkRequestObject }
